/**
 * Domains we consider direct competitors. We still read their content as
 * research input (Tier C in the discovery pipeline) but never link out to them
 * from published articles.
 *
 * Keep this list lowercase, host-only (no scheme, no path). Subdomains are
 * matched automatically via `endsWith('.' + domain)`.
 */
export const COMPETITOR_DOMAINS: readonly string[] = [
  "brightlocal.com",
  "whitespark.ca",
  "localu.org",
  "localseoguide.com",
  "sterlingsky.ca",
  "nearmedia.co",
  "seroundtable.com",
  "searchengineland.com",
];

export function isCompetitorUrl(input: unknown): boolean {
  if (typeof input !== "string" || !input) return false;
  let host: string;
  try {
    host = new URL(input).hostname.toLowerCase();
  } catch {
    return false;
  }
  if (host.startsWith("www.")) host = host.slice(4);
  return COMPETITOR_DOMAINS.some(
    (d) => host === d || host.endsWith("." + d),
  );
}

export function filterCompetitorUrls(urls: unknown): string[] {
  if (!Array.isArray(urls)) return [];
  return urls.filter((u): u is string => typeof u === "string" && !isCompetitorUrl(u));
}

export function filterCompetitorCitations<T extends { url: string }>(
  citations: readonly T[] | undefined | null,
): T[] {
  if (!Array.isArray(citations)) return [];
  return citations.filter((c) => !isCompetitorUrl(c.url));
}

/**
 * Strip competitor links from the trailing `## Sources` section of a markdown
 * article. Leaves the rest of the body untouched. If the section ends up empty,
 * the whole `## Sources` block is removed.
 */
export function stripCompetitorSourcesFromMarkdown(md: string): string {
  if (!md) return md;
  const match = md.match(/\n##\s+Sources\b[^\n]*\n([\s\S]*)$/i);
  if (!match) return md;
  const head = md.slice(0, match.index!);
  const body = match[1];

  const lines = body.split("\n");
  const kept: string[] = [];
  for (const line of lines) {
    // Bullet line with a markdown link: `- [Title](https://...)`
    const linkMatch = line.match(/\]\((https?:\/\/[^)\s]+)\)/);
    if (linkMatch && isCompetitorUrl(linkMatch[1])) continue;
    // Bare URL bullet: `- https://...`
    const bareMatch = line.match(/(https?:\/\/[^\s)]+)/);
    if (bareMatch && /^\s*[-*]\s*<?https?:/i.test(line) && isCompetitorUrl(bareMatch[1])) continue;
    kept.push(line);
  }

  const cleaned = kept.join("\n").replace(/^\s+|\s+$/g, "");
  if (!cleaned) return head.trimEnd() + "\n";
  return `${head}${match[0].slice(0, match[0].indexOf("\n", 1) + 1)}\n${cleaned}\n`;
}

/**
 * Comma-separated string for prompt injection, e.g. "do not cite: …".
 */
export const COMPETITOR_DOMAIN_PROMPT_HINT = COMPETITOR_DOMAINS.join(", ");
